/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles'
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { NumericTextInput } from '../../shared/ui/NumericTextInput'
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import { listSuppliersLocalPaged, getSupplierByIdLocal } from '../../catalog/infra-api'
import { listVariantsForProduct } from '../../catalog/infra-api'
import { useProductVariantsEnabled } from '../../catalog/app-api';
import type { ProductVariant } from '../../catalog/domain-api';
import type { CreatePurchaseOrderInput } from '../infra/purchasesRepoMutations';
import { validatePurchaseOrderForm } from '../domain/purchaseValidation';
import {
  expectedDeliveryFromLeadTime,
  lastUnitCostPrefill,
  minOrderBelowWarning,
  todayLocalIsoDate,
} from '../domain/purchaseSupplierFicha';
import { findDuplicateSupplierInvoiceRef } from '../infra/purchasesRepoRead';
import { PurchaseProductPicker, type PickedPurchaseProduct } from './PurchaseProductPicker';
import { PurchaseVariantPicker } from './PurchaseVariantPicker';
import { PurchaseSupplierField } from './PurchaseSupplierField';
import { PurchaseDraftLineList, type PurchaseDraftLine } from './PurchaseDraftLineList';

import { formatMoney } from '../../shared/format/public-api';
import { ExpenseFiscalSection } from '../../expenses/components/ExpenseFiscalSection';
import {
  emptyExpenseFiscalDraft,
  expenseFiscalDraftIsEmpty,
  expenseFiscalDraftToInput,
  validateExpenseFiscalInvoice,
  type ExpenseFiscalDraft,
} from '../../expenses/domain-api';
import type { ExpenseSettleMethod } from '../../expenses/components/expenseSettlementMethods';
import {
  canSubmitExpensePayment,
  defaultPaidDateForForm,
  resolveSettleMethodFromPaymentForm,
  type ActiveCashSessionInfo,
  type ExpenseAlreadyPaidKind,
  type ExpensePaidFormChoice,
} from '../../expenses/components/expensePaymentFlow';
import { todayIsoDate } from '../../expenses/infra/expenseListSql';
import { getErrorMessage } from '../../shared/errorMessage';
import { buildPurchasePaymentOnReceive } from '../application/purchaseReceptionPayment';
import {
  defaultReceptionPaidForm,
  PurchaseReceptionPaymentBlock,
} from './PurchaseReceptionPaymentBlock';
type Props = {
  visible: boolean;
  mode?: 'order' | 'direct';
  onClose: () => void;
  onSubmit: (params: CreatePurchaseOrderInput) => void;
  branchId: string;
  userId: string | null;
  submitting: boolean;
  fiscalCaptureEnabled?: boolean;
  activeCashSession?: ActiveCashSessionInfo | null;
  expectedCashInDrawer?: number | null;
  onOpenCash?: () => void;
  branches?: Array<{ id: string; name: string }>;
  /** "Duplicar como borrador": proveedor + líneas precargadas desde una OC anulada. */
  initialPrefill?: {
    supplierId: string | null;
    supplierName: string | null;
    items: PurchaseDraftLine[];
  } | null;
  /** Sin proveedores cargados: cierra el modal y navega a Catálogo → Proveedores. */
  onAddSupplier?: () => void;
};

type SupplierOption = { id: string; name: string };

type Composer = {
  product: PickedPurchaseProduct | null;
  quantity: string;
  unitCost: string;
  targetBranchId: string | null;
  /** #713: variante seleccionada (null = parent). */
  variantId: string | null;
  variantName: string | null;
};

const EMPTY_COMPOSER: Composer = {
  product: null,
  quantity: '1',
  unitCost: '',
  targetBranchId: null,
  variantId: null,
  variantName: null,
};


export function PurchaseOrderRegisterModal({
  visible,
  mode = 'order',
  onClose,
  onSubmit,
  branchId,
  userId,
  submitting,
  branches,
  initialPrefill = null,
  onAddSupplier,
  fiscalCaptureEnabled = false,
  activeCashSession = null,
  expectedCashInDrawer = null,
  onOpenCash,
}: Props) {
  const { width, height } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const deskCardHeight = Math.round(height * 0.92);
  const deskMaxWidth = Math.min(1200, Math.max(720, width - 48));
  const backdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  // #713: capability gate para variantes
  const variantsEnabled = useProductVariantsEnabled();

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState<string | null>(null);
  // BUG-13: Cancel flag para evitar race condition en supplier change
  const supplierChangeIdRef = useRef(0);
  const [notes, setNotes] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [items, setItems] = useState<PurchaseDraftLine[]>([]);
  const [composer, setComposer] = useState<Composer>(EMPTY_COMPOSER);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [minOrderAmount, setMinOrderAmount] = useState<number | null>(null);
  const [supplierLeadDays, setSupplierLeadDays] = useState<number | null>(null);
  const [invoiceDuplicateWarning, setInvoiceDuplicateWarning] = useState<string | null>(null);
  // BUG-24: Toggle "Ya pagué" para direct mode
  const [directAlreadyPaid, setDirectAlreadyPaid] = useState(true);
  const [paidForm, setPaidForm] = useState<ExpensePaidFormChoice | null>(null);
  const [alreadyPaidKind, setAlreadyPaidKind] = useState<ExpenseAlreadyPaidKind | null>(null);
  const [nonDrawerMethod, setNonDrawerMethod] = useState<ExpenseSettleMethod>('transfer');
  const [settleNote, setSettleNote] = useState('');
  const [paidDate, setPaidDate] = useState(todayIsoDate());
  const [pastCashDrawerConfirmed, setPastCashDrawerConfirmed] = useState(false);
  const [directFiscalOpen, setDirectFiscalOpen] = useState(false);
  const [directFiscalDraft, setDirectFiscalDraft] = useState<ExpenseFiscalDraft>(
    emptyExpenseFiscalDraft(),
  );
  // Si el usuario fijó una fecha explícita, el lead-time del proveedor no la pisa.
  const expectedDeliveryTouchedRef = useRef(false);
  const { confirm, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const activeCashSessionId = activeCashSession?.session.id ?? null;
  const receptionExpenseDate = todayIsoDate();

  useEffect(() => {
    if (!visible) return;
    const prefill = initialPrefill;
    setSupplierId(prefill?.supplierId ?? null);
    setSupplierName(prefill?.supplierName ?? null);
    setNotes('');
    setExpectedDelivery('');
    setSupplierInvoiceRef('');
    setItems(prefill?.items.map((item) => ({ ...item })) ?? []);
    setComposer(EMPTY_COMPOSER);
    setFormError(null);
    setMinOrderAmount(null);
    setSupplierLeadDays(null);
    setInvoiceDuplicateWarning(null);
    setDirectAlreadyPaid(true);
    setPaidForm(defaultReceptionPaidForm(activeCashSessionId));
    setAlreadyPaidKind(null);
    setNonDrawerMethod('transfer');
    setSettleNote('');
    setPaidDate(todayIsoDate());
    setPastCashDrawerConfirmed(false);
    setDirectFiscalOpen(false);
    setDirectFiscalDraft(emptyExpenseFiscalDraft());
    expectedDeliveryTouchedRef.current = false;
    // OBS-9: Cargar hasta 500 proveedores para evitar missing en el picker
    listSuppliersLocalPaged({ limit: 500, offset: 0 })
      .then((rows) => setSuppliers(rows.map((r) => ({ id: r.id, name: r.name }))))
      .catch(() => setSuppliers([]));
    // Con prefill también traer la ficha del proveedor (pedido mínimo, lead-time).
    if (prefill?.supplierId) {
      void getSupplierByIdLocal(prefill.supplierId)
        .then((supplier) => {
          setMinOrderAmount(supplier?.minimumOrderAmount ?? null);
          setSupplierLeadDays(supplier?.leadTimeDays ?? null);
        })
        .catch(() => setMinOrderAmount(null));
    }
  }, [visible, initialPrefill]);

  const branchNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    (branches ?? []).forEach((b) => {
      map[b.id] = b.name;
    });
    return map;
  }, [branches]);

  const total = items.reduce((sum, item) => {
    const qty = parseDecimalInput(item.quantity) ?? 0;
    const cost = parseDecimalInput(item.unitCost) ?? 0;
    return sum + qty * cost;
  }, 0);

  const parsedItems = items.map((item) => ({
    productId: item.productId,
    productName: item.productName,
    productSku: item.productSku,
    quantity: parseDecimalInput(item.quantity) ?? 0,
    unitCost: item.unitCost ? parseDecimalInput(item.unitCost) : null,
    targetBranchId: item.targetBranchId || null,
    unitOfMeasure: item.unitOfMeasure,
    // #713: variante de destino (null = parent)
    variantId: item.variantId || null,
  }));

  const minOrderWarning = minOrderBelowWarning(minOrderAmount, total);

  const checkInvoiceDuplicate = async (ref: string, supplier: string | null) => {
    if (!supplier || !ref.trim()) {
      setInvoiceDuplicateWarning(null);
      return;
    }
    try {
      const dup = await findDuplicateSupplierInvoiceRef({
        supplierId: supplier,
        invoiceRef: ref,
      });
      setInvoiceDuplicateWarning(
        dup
          ? 'Este n° de factura ya está en otro pedido de este proveedor. Podés guardarlo igual.'
          : null,
      );
    } catch {
      setInvoiceDuplicateWarning(null);
    }
  };

  const handleSupplierChange = async (next: { id: string | null; name: string | null }) => {
    const changeId = ++supplierChangeIdRef.current;
    setSupplierId(next.id);
    setSupplierName(next.name);
    if (!next.id) {
      setMinOrderAmount(null);
      setSupplierLeadDays(null);
      setInvoiceDuplicateWarning(null);
      return;
    }
    try {
      const supplier = await getSupplierByIdLocal(next.id);
      // BUG-13: Descartar resultado si el usuario cambió de supplier mientras tanto
      if (supplierChangeIdRef.current !== changeId) return;
      setMinOrderAmount(supplier?.minimumOrderAmount ?? null);
      setSupplierLeadDays(supplier?.leadTimeDays ?? null);
      // BUG-4 fix: una fecha explícita del usuario siempre gana sobre el lead-time.
      if (!expectedDeliveryTouchedRef.current) {
        const expected = expectedDeliveryFromLeadTime(supplier?.leadTimeDays ?? null, todayLocalIsoDate());
        if (expected) setExpectedDelivery(expected);
      }
    } catch {
      if (supplierChangeIdRef.current !== changeId) return;
      setMinOrderAmount(null);
      setSupplierLeadDays(null);
    }
    void checkInvoiceDuplicate(supplierInvoiceRef, next.id);
  };

  const canSubmit =
    Boolean(supplierId) &&
    validatePurchaseOrderForm({
      supplierId,
      notes,
      expectedDelivery,
      items: parsedItems,
    }).ok;

  const title = mode === 'direct' ? 'Comprar ahora' : 'Nueva orden de compra';
  // Proto: en compra directa el total viaja dentro del CTA («Recibir y cargar · $X»).
  const submitLabel =
    mode === 'direct' ? `Recibir y cargar · ${formatMoney(total)}` : 'Guardar borrador';

  const handleAddLine = () => {
    if (submitting) return;
    if (!composer.product) {
      setFormError('Elegí un producto.');
      return;
    }
    const product = composer.product;
    const qty = parseDecimalInput(composer.quantity) ?? 0;
    const cost = parseDecimalInput(composer.unitCost);
    const check = validatePurchaseOrderForm({
      supplierId: supplierId ?? 'pending',
      items: [
        {
          productId: product.id,
          quantity: qty,
          unitCost: cost ?? null,
          unitOfMeasure: product.unitOfMeasure,
        },
      ],
    });
    if (!check.ok) {
      setFormError(check.message);
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        quantity: composer.quantity,
        unitCost: composer.unitCost,
        unitOfMeasure: product.unitOfMeasure,
        targetBranchId: composer.targetBranchId,
        // #713: variante de destino
        variantId: composer.variantId,
        variantName: composer.variantName,
      },
    ]);
    setComposer(EMPTY_COMPOSER);
    setFormError(null);
  };

  const settleMethod = useMemo(
    () =>
      paidForm
        ? resolveSettleMethodFromPaymentForm({ paidForm, alreadyPaidKind, nonDrawerMethod })
        : 'transfer',
    [paidForm, alreadyPaidKind, nonDrawerMethod],
  );

  const handleSubmit = async () => {
    if (submitting) return;
    const validation = validatePurchaseOrderForm({
      supplierId,
      notes,
      expectedDelivery,
      items: parsedItems,
    });
    if (!validation.ok) {
      setFormError(validation.message);
      return;
    }
    let directFiscalPayload: ReturnType<typeof expenseFiscalDraftToInput> = null;
    if (mode === 'direct' && fiscalCaptureEnabled && !expenseFiscalDraftIsEmpty(directFiscalDraft)) {
      directFiscalPayload = expenseFiscalDraftToInput(directFiscalDraft);
      if (!directFiscalPayload) {
        setFormError('Completá el comprobante fiscal o dejalo en “Sin comprobante”.');
        return;
      }
      const fiscalError = validateExpenseFiscalInvoice(directFiscalPayload);
      if (fiscalError) {
        setFormError(fiscalError);
        return;
      }
    }
    let paymentOnReceive: CreatePurchaseOrderInput['paymentOnReceive'];
    if (mode === 'direct' && directAlreadyPaid) {
      if (
        !canSubmitExpensePayment({
          paymentStatus: 'paid',
          paidForm,
          alreadyPaidKind,
          settleMethod,
          activeCashSessionId,
          settleNote,
          pastCashDrawerConfirmed,
          expenseDate: receptionExpenseDate,
          amount: total,
        })
      ) {
        setFormError('Completá cómo se pagó.');
        return;
      }
      if (
        settleMethod === 'cash_drawer' &&
        expectedCashInDrawer != null &&
        total > expectedCashInDrawer
      ) {
        const ok = await confirm({
          title: 'Sale más plata de la que hay en caja',
          message: `Querés sacar ${formatMoney(total)} y en caja hay ${formatMoney(expectedCashInDrawer)}. ¿Lo registrás igual?`,
          confirmLabel: 'Registrar',
        });
        if (!ok) return;
      }
      try {
        paymentOnReceive = buildPurchasePaymentOnReceive({
          paidForm,
          alreadyPaidKind,
          nonDrawerMethod,
          settleNote,
          paidDate,
          activeCashSessionId,
        });
      } catch (err) {
        setFormError(getErrorMessage(err));
        return;
      }
    }

    setFormError(null);
    onSubmit({
      purchaseOrderId: crypto.randomUUID(),
      branchId,
      supplierId,
      supplierName,
      status: mode === 'direct' ? 'sent' : 'draft',
      origin: mode === 'direct' ? 'direct' : 'order',
      notes: notes || null,
      expectedDelivery: expectedDelivery || null,
      supplierInvoiceRef: supplierInvoiceRef || null,
      createdBy: userId,
      createdAt: new Date().toISOString(),
      items: parsedItems,
      // BUG-24: Pasar alreadyPaid del toggle en direct mode
      alreadyPaid: mode === 'direct' ? directAlreadyPaid : undefined,
      paymentOnReceive,
      directFiscal: mode === 'direct' ? directFiscalPayload : undefined,
    });
  };

  const otherBranches = (branches ?? []).filter((b) => b.id !== branchId);
  const showBranchPicker = otherBranches.length > 0;

  const composerBlock = (
    <View style={styles.composer}>
      <ModalFormField label="Producto">
        <PurchaseProductPicker
          branchId={branchId}
          selected={
            composer.product
              ? {
                  id: composer.product.id,
                  name: composer.product.name,
                  sku: composer.product.sku,
                  supplierItemCode: composer.product.supplierItemCode,
                }
              : null
          }
          disabled={submitting}
          onSelect={(row) =>
            setComposer((prev) => ({
              ...prev,
              product: row,
              // #713: resetear variante al cambiar de producto
              variantId: prev.product?.id !== row.id ? null : prev.variantId,
              variantName: prev.product?.id !== row.id ? null : prev.variantName,
              // BUG-32: Solo sobrescribir el costo si el usuario no tipeó manualmente,
              // o si es un producto distinto, o si lastUnitCost llegó (no es null)
              unitCost:
                prev.product?.id !== row.id
                  ? lastUnitCostPrefill(row.lastUnitCost)
                  : prev.unitCost === ''
                    ? lastUnitCostPrefill(row.lastUnitCost)
                    : prev.unitCost,
            }))
          }
          onClear={() => setComposer((prev) => ({ ...prev, product: null, unitCost: '', variantId: null, variantName: null }))}
        />
      </ModalFormField>
      {/* #713: variant picker — solo se renderiza si el producto tiene variantes activas */}
      {composer.product && variantsEnabled && (
        <PurchaseVariantPicker
          productId={composer.product.id}
          selectedVariantId={composer.variantId}
          onSelect={(variantId, variantName) =>
            setComposer((prev) => ({ ...prev, variantId, variantName }))
          }
          disabled={submitting}
        />
      )}
      <View style={styles.composerNumbers}>
        <View style={styles.flex1}>
          <ModalFormField label="Cantidad">
            <NumericTextInput
              kind="decimalMoney"
              value={composer.quantity}
              onChangeText={(v) => setComposer((prev) => ({ ...prev, quantity: v }))}
              placeholder="1"
              style={modalExperience.formInput}
              placeholderTextColor={ticketColors.ink3}
              editable={!submitting}
            />
          </ModalFormField>
        </View>
        <View style={styles.flex1}>
          <ModalFormField label="Costo unitario">
            <NumericTextInput
              kind="decimalMoney"
              value={composer.unitCost}
              onChangeText={(v) => setComposer((prev) => ({ ...prev, unitCost: v }))}
              placeholder="0"
              style={modalExperience.formInput}
              placeholderTextColor={ticketColors.ink3}
              editable={!submitting}
            />
          </ModalFormField>
        </View>
        <TouchableOpacity
          style={[styles.addBtn, submitting && styles.saveBtnDisabled]}
          onPress={handleAddLine}
          disabled={submitting}
          accessibilityRole="button"
          accessibilityLabel="Agregar"
        >
          <Text style={styles.addBtnText}>Agregar</Text>
        </TouchableOpacity>
      </View>
      {showBranchPicker ? (
        <View style={styles.branchPickerRow}>
          <TouchableOpacity
            style={[styles.branchChip, !composer.targetBranchId && styles.branchChipSelected]}
            onPress={() => setComposer((prev) => ({ ...prev, targetBranchId: null }))}
          >
            <Text style={!composer.targetBranchId ? styles.branchChipTextSelected : styles.branchChipText}>
              Esta sucursal
            </Text>
          </TouchableOpacity>
          {otherBranches.map((b) => (
            <TouchableOpacity
              key={b.id}
              style={[styles.branchChip, composer.targetBranchId === b.id && styles.branchChipSelected]}
              onPress={() => setComposer((prev) => ({ ...prev, targetBranchId: b.id }))}
            >
              <Text
                style={
                  composer.targetBranchId === b.id ? styles.branchChipTextSelected : styles.branchChipText
                }
              >
                {b.name}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
    </View>
  );

  const supplierBlock =
    suppliers.length === 0 ? (
      // Sin proveedores el picker queda vacío: mensaje + acción directa a Catálogo.
      <View style={styles.supplierEmptyRow}>
        <Text style={styles.mutedText}>Para armar la orden primero cargá un proveedor.</Text>
        {onAddSupplier ? (
          <TouchableOpacity
            onPress={onAddSupplier}
            accessibilityRole="button"
            accessibilityLabel="Cargar proveedor"
            testID="purchases.order.add-supplier"
          >
            <Text style={styles.supplierEmptyLink}>Cargar proveedor</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    ) : (
      <View>
        <PurchaseSupplierField
          suppliers={suppliers}
          supplierId={supplierId}
          onSupplierChange={({ id, name }) => {
            void handleSupplierChange({ id, name });
          }}
          disabled={submitting}
        />
        {/* Proto: hint de la ficha bajo el select (lead-time + mínimo). */}
        {supplierId && (supplierLeadDays != null || minOrderAmount != null) ? (
          <Text style={styles.supplierHint}>
            {[
              supplierLeadDays != null ? `Entrega habitual ${supplierLeadDays} días` : null,
              minOrderAmount != null ? `mínimo de compra ${formatMoney(minOrderAmount)}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        ) : null}
      </View>
    );

  const extraMetaBlock = (
    <View style={isMobile ? styles.metaStack : styles.metaRow}>
      <View style={styles.flex1}>
        <ModalFormField label="N° de factura">
          <TextInput
            style={modalExperience.formInput}
            value={supplierInvoiceRef}
            onChangeText={(value) => {
              setSupplierInvoiceRef(value);
              if (!value.trim()) setInvoiceDuplicateWarning(null);
            }}
            onBlur={() => {
              void checkInvoiceDuplicate(supplierInvoiceRef, supplierId);
            }}
            placeholder="Factura del proveedor"
            placeholderTextColor={ticketColors.ink3}
            editable={!submitting}
            accessibilityLabel="N° de factura"
          />
        </ModalFormField>
      </View>
      <View style={styles.flex1}>
        <ModalFormField label="Entrega esperada">
          <InventoryIsoDateField
            value={expectedDelivery}
            onChange={(next) => {
              expectedDeliveryTouchedRef.current = true;
              setExpectedDelivery(next);
            }}
            emptyLabel="Sin fecha"
            ariaLabel="Entrega esperada"
            clearButtonLabel="Quitar fecha"
          />
        </ModalFormField>
      </View>
    </View>
  );

  const notesBlock = (
    <ModalFormField label="Nota">
      <TextInput
        style={[modalExperience.formInput, styles.notesInput]}
        value={notes}
        onChangeText={setNotes}
        placeholder="Interna — no la ve el proveedor"
        placeholderTextColor={ticketColors.ink3}
        editable={!submitting}
        multiline
      />
    </ModalFormField>
  );

  const isDirty =
    Boolean(supplierId) ||
    notes.trim() !== '' ||
    expectedDelivery !== '' ||
    supplierInvoiceRef.trim() !== '' ||
    items.length > 0 ||
    composer.product != null ||
    composer.quantity !== '1' ||
    composer.unitCost !== '' ||
    !directAlreadyPaid;

  const requestClose = () => {
    if (submitting) return;
    if (!isDirty) {
      onClose();
      return;
    }
    void confirm({
      title: mode === 'direct' ? 'Descartar compra' : 'Descartar orden',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) onClose();
    });
  };

  const errorBlock = formError ? (
    <View style={styles.errorBox}>
      <Text style={styles.formError}>{formError}</Text>
    </View>
  ) : null;
  // Proto: warnings como warn-box inline — factura duplicada bajo el campo,
  // mínimo de compra al pie de la columna de items.
  const invoiceWarnBlock = invoiceDuplicateWarning ? (
    <View style={styles.warningBox}>
      <Text style={styles.formWarning} accessibilityRole="text">
        {invoiceDuplicateWarning}
      </Text>
    </View>
  ) : null;
  const minOrderWarnBlock = minOrderWarning ? (
    <View style={styles.warningBox}>
      <Text style={styles.formWarning} accessibilityRole="text">
        {minOrderWarning}
      </Text>
    </View>
  ) : null;
  // Proto: info-box al pie de la columna izquierda (contexto del modo).
  const infoBlock = (
    <View style={styles.infoBox}>
      <Text style={styles.infoBoxText}>
        {mode === 'direct'
          ? 'Registra recepción, stock y gasto de una. Si elegís efectivo de la caja, descuenta el turno abierto. Destildá "Ya pagué" y queda como deuda al proveedor.'
          : 'Borrador → la marcás Enviada cuando se la mandás al proveedor (compartir por WhatsApp desde el detalle).'}
      </Text>
    </View>
  );

  const linesBlock = (
    <PurchaseDraftLineList
      items={items}
      isMobile={isMobile}
      submitting={submitting}
      branchNamesById={branchNamesById}
      onChange={(id, field, value) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
      }}
      onRemove={(id) => setItems((prev) => prev.filter((item) => item.id !== id))}
    />
  );

  const totalRow = (
    <View style={styles.totalRow}>
      <Text style={styles.totalLabel}>{items.length === 1 ? '1 producto' : `${items.length} productos`}</Text>
      <Text style={styles.totalValue}>{formatMoney(total)}</Text>
    </View>
  );

  const directFiscalBlock =
    mode === 'direct' && fiscalCaptureEnabled ? (
      <View style={styles.fiscalBlock}>
        <TouchableOpacity
          accessibilityRole="button"
          testID="purchases.direct.fiscal.toggle"
          onPress={() => setDirectFiscalOpen((open) => !open)}
          disabled={submitting}
        >
          <Text style={styles.fiscalToggle}>
            {directFiscalOpen ? '▾' : '▸'} ¿Tenés factura del proveedor?
          </Text>
        </TouchableOpacity>
        {directFiscalOpen ? (
          <ExpenseFiscalSection
            draft={directFiscalDraft}
            onChange={setDirectFiscalDraft}
            expenseAmount={total > 0 ? total : null}
            submitting={submitting}
            branchId={branchId}
            testIDPrefix="purchases.direct.fiscal"
          />
        ) : (
          <Text style={styles.fiscalHint}>
            Sin factura por ahora — podés cargarla después en el detalle del pedido.
          </Text>
        )}
      </View>
    ) : null;

  // BUG-24: Toggle "Ya pagué" solo en direct mode
  const directAlreadyPaidRow = mode === 'direct' ? (
    <>
      <View style={styles.expenseToggleRow}>
        <TouchableOpacity
          style={[styles.checkbox, directAlreadyPaid && styles.checkboxActive]}
          onPress={() => {
            const next = !directAlreadyPaid;
            setDirectAlreadyPaid(next);
            if (next) {
              setPaidForm(defaultReceptionPaidForm(activeCashSessionId));
            }
          }}
          accessibilityRole="checkbox"
          accessibilityLabel="Ya pagué al recibir"
          accessibilityHint="Si pagás con efectivo de la caja, elegí ese medio abajo para que descuente el turno."
        >
          {directAlreadyPaid ? <Text style={styles.checkboxTick}>✓</Text> : null}
        </TouchableOpacity>
        <Text style={styles.expenseToggleLabel}>Ya pagué al recibir</Text>
      </View>
      {directAlreadyPaid ? (
        <PurchaseReceptionPaymentBlock
          expenseDate={receptionExpenseDate}
          paidForm={paidForm}
          onPaidFormChange={setPaidForm}
          alreadyPaidKind={alreadyPaidKind}
          onAlreadyPaidKindChange={setAlreadyPaidKind}
          nonDrawerMethod={nonDrawerMethod}
          onNonDrawerMethodChange={setNonDrawerMethod}
          settleNote={settleNote}
          onSettleNoteChange={setSettleNote}
          paidDate={paidDate}
          onPaidDateChange={setPaidDate}
          activeCashSession={activeCashSession}
          pastCashDrawerConfirmed={pastCashDrawerConfirmed}
          onPastCashDrawerConfirmedChange={setPastCashDrawerConfirmed}
          submitting={submitting}
          onOpenCash={onOpenCash}
          testIDPrefix="purchases.direct.payment"
        />
      ) : null}
    </>
  ) : null;

  if (isMobile) {
    return (
      <>
      <Modal visible={visible} animationType="slide" transparent onRequestClose={requestClose} accessibilityViewIsModal>
        <View style={ticketChrome.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={requestClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap}>
            <View
              style={[
                ticketChrome.sheetCard,
                // maxHeight en px: el % no resuelve contra KeyboardAvoidingView (alto auto en web)
                { maxHeight: height * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) },
              ]}
            >
              <View style={ticketChrome.grabber} />
              <View style={ticketChrome.sheetHeader}>
                <View style={styles.sheetHeaderText}>
                  <Text style={styles.sheetKicker}>
                    {mode === 'direct' ? 'Compras · Compra directa' : 'Compras'}
                  </Text>
                  <Text style={ticketChrome.sheetSubtitle}>{title}</Text>
                </View>
                <TouchableOpacity
                  onPress={requestClose}
                  style={ticketChrome.sheetCloseBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.sheetSticky}>
                {errorBlock}
                {supplierBlock}
                {composerBlock}
              </View>
              <ModalAwareScrollView
                style={styles.sheetScroll}
                contentContainerStyle={styles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {extraMetaBlock}
                {invoiceWarnBlock}
                {notesBlock}
                {linesBlock}
                {minOrderWarnBlock}
                {directFiscalBlock}
                {directAlreadyPaidRow}
                {infoBlock}
              </ModalAwareScrollView>
              <View style={ticketChrome.foot}>
                {totalRow}
                <View style={styles.footActions}>
                  <TouchableOpacity
                    style={[ticketChrome.btnGhost, { flex: 1, minWidth: 0 }]}
                    onPress={requestClose}
                  >
                    <Text style={ticketChrome.btnGhostText}>Cancelar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      ticketChrome.btnGold,
                      { flex: 1, minWidth: 0 },
                      (!canSubmit || submitting) && styles.saveBtnDisabled,
                    ]}
                    onPress={() => void handleSubmit()}
                    disabled={!canSubmit || submitting}
                    accessibilityRole="button"
                    accessibilityLabel={submitLabel}
                  >
                    <Text style={ticketChrome.btnGoldText}>{submitLabel}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </KeyboardAvoidingView>
          {DiscardConfirmModal}
        </View>
      </Modal>
      </>
    );
  }

  // Proto: orden = 2 columnas (izq proveedor/meta/nota, der composer+items).
  // Compra directa = columna única angosta.
  const deskBody =
    mode === 'direct' ? (
      <ScrollView
        style={styles.deskDirectScroll}
        contentContainerStyle={styles.deskDirectContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {supplierBlock}
        {extraMetaBlock}
        {invoiceWarnBlock}
        {notesBlock}
        <Text style={styles.secTitle}>Items</Text>
        {composerBlock}
        {linesBlock}
        {minOrderWarnBlock}
        {directFiscalBlock}
        {directAlreadyPaidRow}
        {infoBlock}
      </ScrollView>
    ) : (
      <View style={styles.deskColumns}>
        <ScrollView
          style={styles.deskColLeft}
          contentContainerStyle={styles.deskColLeftContent}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          showsVerticalScrollIndicator={false}
        >
          {supplierBlock}
          {extraMetaBlock}
          {invoiceWarnBlock}
          {notesBlock}
          <View style={styles.spacerFill} />
          {infoBlock}
        </ScrollView>
        <View style={styles.deskColRight}>
          <Text style={styles.secTitle}>Items</Text>
          {composerBlock}
          <ScrollView
            style={styles.deskLinesScroll}
            contentContainerStyle={styles.linesScrollContent}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
          >
            {linesBlock}
          </ScrollView>
          {minOrderWarnBlock}
        </View>
      </View>
    );

  return (
    <>
    <Modal visible={visible} animationType="fade" transparent onRequestClose={requestClose} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            mode === 'direct' ? null : modalExperience.modalWidthWide,
            {
              width: '100%',
              // Proto: "Comprar ahora" es un modal angosto de una sola columna.
              maxWidth: mode === 'direct' ? 620 : deskMaxWidth,
              alignSelf: 'center',
              height: deskCardHeight,
              maxHeight: '92%',
              overflow: 'hidden',
            },
          ]}
        >
          <ModalScreenHeader
            title={title}
            kicker={mode === 'direct' ? 'Compras · Compra directa' : 'Compras'}
            subtitle={
              mode === 'direct'
                ? 'Sin pedido previo — la mercadería entra a stock de una.'
                : 'Armá el pedido al proveedor. El stock se suma cuando recibís.'
            }
            onClose={requestClose}
          />
          {errorBlock}
          {deskBody}
          <View style={[posUi.modalActions, modalExperience.footerActions, styles.deskFootRow]}>
            {mode === 'direct' ? (
              <View style={styles.spacerFill} />
            ) : (
              <Text style={styles.deskFootHint}>
                {items.length === 1 ? '1 producto' : `${items.length} productos`}
                {' · total '}
                <Text style={styles.deskFootTotal}>{formatMoney(total)}</Text>
              </Text>
            )}
            <View style={styles.footButtons}>
              <TouchableOpacity
                style={[posUi.secondaryButton, styles.footCancelBtn]}
                onPress={requestClose}
              >
                <Text style={posUi.secondaryButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  posUi.primaryButton,
                  styles.footPrimaryBtn,
                  (!canSubmit || submitting) && posUi.disabledCard,
                ]}
                onPress={() => void handleSubmit()}
                disabled={!canSubmit || submitting}
                accessibilityRole="button"
                accessibilityLabel={submitLabel}
              >
                <Text style={posUi.primaryButtonText}>{submitLabel}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        {DiscardConfirmModal}
      </View>
    </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetSticky: {
    paddingHorizontal: 20,
    paddingBottom: spacing.sm,
  },
  sheetHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  sheetKicker: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    marginBottom: 2,
  },
  sheetScroll: {
    flexShrink: 1,
    minHeight: 0,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.sm,
  },
  deskLinesScroll: {
    flex: 1,
    minHeight: 160,
  },
  linesScrollContent: {
    paddingBottom: spacing.xs,
  },
  // Desktop 2 columnas (proto): proveedor/meta a la izquierda con hairline
  // divisoria, composer + líneas a la derecha. Sin padding externo — lo lleva
  // cada columna (16px 20px) como en el proto.
  deskColumns: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'row',
  },
  deskColLeft: {
    // Split 40/60: los items (corazón del form) llevan la columna ancha.
    flex: 0.8,
    minWidth: 0,
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
  },
  deskColLeftContent: {
    flexGrow: 1,
    gap: 14,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  deskColRight: {
    flex: 1.2,
    minWidth: 0,
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  deskDirectScroll: {
    flex: 1,
    minHeight: 0,
  },
  deskDirectContent: {
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingBottom: spacing.md,
  },
  spacerFill: {
    flex: 1,
  },
  secTitle: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
  },
  supplierHint: {
    fontSize: 11.5,
    color: ticketColors.ink3,
    marginTop: 5,
  },
  notesInput: {
    minHeight: 52,
    textAlignVertical: 'top',
    paddingTop: 10,
  },
  infoBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    // Proto .info-box: fondo tile neutro (#FAFBFA), no teal.
    backgroundColor: ticketColors.paper2,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  infoBoxText: {
    fontSize: 12.5,
    lineHeight: 19,
    color: ticketColors.ink2,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  errorBox: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(220, 38, 38, 0.30)',
    backgroundColor: 'rgba(220, 38, 38, 0.06)',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  formError: {
    color: ticketColors.bad,
    fontSize: 13,
    fontWeight: '600',
  },
  warningBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingVertical: 9,
    paddingHorizontal: 11,
    gap: 4,
  },
  formWarning: {
    color: ticketColors.warn,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '600',
  },
  mutedText: {
    color: ticketColors.ink3,
    fontSize: 13,
  },
  supplierEmptyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  supplierEmptyLink: {
    color: ticketColors.ink,
    fontSize: 13,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  metaRow: {
    flexDirection: 'row',
    gap: 8,
  },
  metaStack: {
    gap: 0,
  },
  composer: {
    gap: spacing.xs,
    paddingTop: spacing.xs,
  },
  composerNumbers: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-end',
  },
  flex1: {
    flex: 1,
    minWidth: 0,
  },
  addBtn: {
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 2,
  },
  addBtnText: {
    color: ticketColors.goldInk,
    fontSize: 13,
    fontWeight: '700',
  },
  branchPickerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  branchChip: {
    paddingVertical: 8,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  branchChipSelected: {
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
  },
  branchChipText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  branchChipTextSelected: {
    fontSize: 12.5,
    color: ticketColors.brandInk,
    fontWeight: '600',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  footActions: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  // BUG-24: Estilos para el toggle "Ya pagué" en direct mode (proto .check-row/.check)
  expenseToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 4,
    width: '100%',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1.5,
    borderColor: ticketColors.rule,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxActive: {
    backgroundColor: ticketColors.brandPrimary,
    borderColor: ticketColors.brandPrimary,
  },
  checkboxTick: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  expenseToggleLabel: {
    fontSize: 13.5,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  deskFootRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  deskFootHint: {
    flex: 1,
    minWidth: 0,
    fontSize: 11.5,
    color: ticketColors.ink3,
  },
  deskFootTotal: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  footButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  footCancelBtn: {
    minWidth: 120,
  },
  footPrimaryBtn: {
    minWidth: 170,
  },
  fiscalBlock: {
    marginTop: 8,
    gap: 8,
  },
  fiscalToggle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  fiscalHint: {
    fontSize: 12.5,
    color: ticketColors.ink3,
    lineHeight: 18,
  },
});
